<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ $t('ledger_quote_new') }}
      </h4>
      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_quote_date')">
            <b-form-input
              v-model="form.quote_date"
              type="date"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_quote_payment_mode')">
            <b-form-select
              v-model="form.payment_mode"
              :options="paymentModes"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_quote_tax')">
            <b-form-input
              v-model.number="form.tax_amount"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group :label="$t('ledger_account')">
            <gl-account-select
              v-model="form.gl_account_id"
              :accounts="accounts"
              posting-only
              :loading="loadingAccounts"
              :placeholder="$t('ledger_account')"
            />
            <small
              v-if="submitted && !form.gl_account_id"
              class="text-danger"
            >
              {{ $t('ledger_quote_account_required') }}
            </small>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_inv_customer')">
            <v-select
              :value="customerOption"
              :dir="isRtl ? 'rtl' : 'ltr'"
              :options="customerOptions"
              label="label"
              :filterable="false"
              :placeholder="$t('ledger_inv_customer')"
              class="ledger-account-select"
              @search="searchCustomers"
              @input="onCustomer"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_project')">
            <v-select
              :value="projectOption"
              :dir="isRtl ? 'rtl' : 'ltr'"
              :options="projectOptions"
              label="label"
              :filterable="false"
              :placeholder="$t('ledger_payroll_project')"
              class="ledger-account-select"
              @search="searchProjects"
              @input="onProject"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group :label="$t('ledger_notes')">
            <b-form-textarea
              v-model="form.notes"
              rows="2"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div
        v-for="(line, index) in form.lines"
        :key="index"
        class="mb-50"
      >
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group :label="$t('ledger_description')">
              <b-form-input v-model="line.description" />
            </b-form-group>
          </b-col>
          <b-col
            cols="4"
            md="2"
          >
            <b-form-group :label="$t('ledger_maps_qty')">
              <b-form-input
                v-model.number="line.quantity"
                type="number"
                min="0.01"
                step="0.01"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group :label="$t('ledger_maps_unit_price')">
              <b-form-input
                v-model.number="line.unit_price"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="2"
            md="1"
            class="d-flex align-items-end mb-1"
          >
            <b-button
              v-if="form.lines.length > 1"
              variant="outline-danger"
              size="sm"
              @click="form.lines.splice(index, 1)"
            >
              ×
            </b-button>
          </b-col>
        </b-row>
        <div class="text-muted small ledger-num">
          {{ money(lineTotal(line)) }}
        </div>
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        class="mb-1"
        @click="addLine"
      >
        {{ $t('ledger_inv_add_line') }}
      </b-button>

      <div class="ledger-num mb-1">
        <div>{{ $t('ledger_quote_subtotal') }} {{ money(subtotal) }}</div>
        <div>{{ $t('ledger_quote_tax') }} {{ money(tax) }}</div>
        <strong>{{ $t('ledger_inv_total') }} {{ money(total) }}</strong>
      </div>
      <p class="text-muted small">
        {{ $t('ledger_quote_type_hint') }}
      </p>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="saving"
        @click="save"
      >
        {{ saving ? $t('ledger_saving') : $t('ledger_quote_save') }}
      </b-button>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Projects from '@/api/projects/projects'
import Customers from '@/api/customers/customers'
import { fetchAllChartAccounts } from '@/api/accounting/chartOfAccounts'
import { createQuote } from '@/api/accounting/quotes'
import GlAccountSelect from '../components/GlAccountSelect.vue'
import {
  listFrom, oneFrom, formatLedgerMoney, roundMoney, todayIso, apiErrorMessage,
} from '@/utils/ledger'

const projectsApi = new Projects()
const customersApi = new Customers()

function blankLine() {
  return { description: '', quantity: 1, unit_price: null }
}

export default {
  name: 'QuoteForm',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BButton,
    vSelect,
    GlAccountSelect,
  },
  data() {
    return {
      saving: false,
      submitted: false,
      loadingAccounts: false,
      accounts: [],
      customerOptions: [],
      projectOptions: [],
      customerOption: null,
      projectOption: null,
      customerTimer: null,
      projectTimer: null,
      form: {
        quote_date: todayIso(),
        gl_account_id: null,
        payment_mode: 'نقدي',
        tax_amount: 0,
        notes: '',
        customer_id: null,
        project_id: null,
        lines: [blankLine()],
      },
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    paymentModes() {
      return [
        { value: 'نقدي', text: this.$t('ledger_quote_cash') },
        { value: 'اجل', text: this.$t('ledger_quote_credit') },
      ]
    },
    tax() {
      const n = Number(this.form.tax_amount)
      return Number.isFinite(n) && n > 0 ? roundMoney(n) : 0
    },
    subtotal() {
      return this.form.lines.reduce((sum, line) => roundMoney(sum + this.lineTotal(line)), 0)
    },
    total() {
      return roundMoney(this.subtotal + this.tax)
    },
  },
  mounted() {
    this.loadAccounts()
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    lineTotal(line) {
      const qty = Number(line.quantity)
      const price = Number(line.unit_price)
      if (!Number.isFinite(qty) || !Number.isFinite(price)) return 0
      return roundMoney(qty * price)
    },
    addLine() {
      this.form.lines.push(blankLine())
    },
    onCustomer(option) {
      this.customerOption = option
      this.form.customer_id = option ? option.id : null
    },
    onProject(option) {
      this.projectOption = option
      this.form.project_id = option ? option.id : null
    },
    async loadAccounts() {
      this.loadingAccounts = true
      try {
        const rows = await fetchAllChartAccounts({
          is_active: true,
          accepts_posting: true,
          per_page: 100,
        })
        this.accounts = rows.filter(account => account.accepts_posting && account.is_active)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loadingAccounts = false
      }
    },
    searchProjects(search, loading) {
      const q = (search || '').trim()
      if (q.length < 1) return
      if (loading) loading(true)
      clearTimeout(this.projectTimer)
      this.projectTimer = setTimeout(async () => {
        try {
          const res = await projectsApi.listProjects({ search: q, per_page: 20, page: 1 })
          this.projectOptions = listFrom(res).map(project => ({
            id: project.id,
            label: `${project.project_number || project.id} — ${project.name || ''}`,
          }))
        } catch (error) {
          this.projectOptions = []
        } finally {
          if (loading) loading(false)
        }
      }, 300)
    },
    searchCustomers(search, loading) {
      const q = (search || '').trim()
      if (q.length < 1) return
      if (loading) loading(true)
      clearTimeout(this.customerTimer)
      this.customerTimer = setTimeout(async () => {
        try {
          const res = await customersApi.listCustomers({ search: q, per_page: 20, page: 1 })
          this.customerOptions = listFrom(res).map(customer => ({
            id: customer.id,
            label: customer.name || customer.customer_number || `#${customer.id}`,
          }))
        } catch (error) {
          this.customerOptions = []
        } finally {
          if (loading) loading(false)
        }
      }, 300)
    },
    validLines() {
      return this.form.lines.filter(line => {
        const qty = Number(line.quantity)
        const price = Number(line.unit_price)
        return String(line.description || '').trim()
          && Number.isFinite(qty)
          && qty >= 0.01
          && Number.isFinite(price)
          && price >= 0
      })
    },
    async save() {
      this.submitted = true
      const lines = this.validLines()
      if (!this.form.quote_date || !this.form.gl_account_id || !this.form.payment_mode || !lines.length) {
        this.toast(this.$t('ledger_quote_invalid'), 'danger')
        return
      }
      this.saving = true
      try {
        const res = await createQuote({
          quote_date: this.form.quote_date,
          gl_account_id: this.form.gl_account_id,
          payment_mode: this.form.payment_mode,
          tax_amount: this.tax,
          notes: this.form.notes || null,
          customer_id: this.form.customer_id || null,
          project_id: this.form.project_id || null,
          lines: lines.map(line => ({
            description: String(line.description).trim(),
            quantity: Number(line.quantity),
            unit_price: Number(line.unit_price),
          })),
        })
        const quote = oneFrom(res)
        this.toast(this.$t('ledger_quote_saved'), 'success')
        this.$router.push({ name: 'ledger-quotes-view', params: { id: quote.id } })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.saving = false
      }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
.ledger-num { font-variant-numeric: tabular-nums; }
</style>
